/* eslint-disable @typescript-eslint/no-explicit-any */

import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ElementRef,
  inject,
  NgZone,
  signal,
  OnDestroy,
  OnInit,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { HeaderComponent } from 'nebula-ui-kit';
import { LucideAngularModule } from 'lucide-angular';
import { NOTIFICATION_PANEL_OPENER } from '../../../../notification-panel-opener.token';
import {
  AuthService,
  NotificationService,
  VAULT_BACKEND_NOTIFICATIONS,
  VaultAuthMeService,
} from '@nebula/shared-vault/data-access';
import { merge, interval, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import {
  VaultGlobalMenuSearchService,
  type VaultMenuSearchOption,
} from '../../../../data-access/vault-global-menu-search.service';
import { VAULT_MAX_OPEN_TABS } from '../../../../../const/src/lib/layout.constants';

@Component({
  selector: 'app-topbar-wrapper-2',
  templateUrl: './topbar-wrapper.component.html',
  styleUrls: ['./topbar-wrapper.component.scss'],
  standalone: true,
  imports: [CommonModule, MatMenuModule, HeaderComponent, LucideAngularModule],
})
export class TopbarWrapperComponent2 implements OnInit, OnDestroy {
  private readonly openNotificationPanel = inject(NOTIFICATION_PANEL_OPENER, {
    optional: true,
  });
  private readonly notificationService = inject(NotificationService);
  private readonly backendNotifications = inject(VAULT_BACKEND_NOTIFICATIONS, { optional: true });
  private readonly authService = inject(AuthService);
  private readonly authMe = inject(VaultAuthMeService);
  private readonly menuSearch = inject(VaultGlobalMenuSearchService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();
  private readonly notificationCountRefresh$ = new Subject<void>();

  @Input() userName = '';
  @Input() isDarkMode = false;
  @Input() logoUrl = '';
  @Input() itemsProfileMenu: Array<{ id: number; action: string }> = [];


  readonly userFullName = signal<string>('');

  readonly userRoles = signal<string>('');

  readonly notificationCount = signal(0);

  readonly searchResults = signal<VaultMenuSearchOption[]>([]);
  readonly showSearchResults = signal(false);
  readonly searchToast = signal<string | null>(null);
  private searchToastTimeout: ReturnType<typeof setTimeout> | null = null;

  @Output() darkModeToggle = new EventEmitter<void>();
  @Output() profileClick = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  private readonly ngZone = inject(NgZone);

  @ViewChild('triggerButton') triggerButton!: ElementRef<HTMLButtonElement>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('hidden_button') hiddenButton!: ElementRef<HTMLButtonElement>;

  private readonly MENU_WIDTH = 170;
  private readonly MENU_OFFSET = 8;
  private isMenuOpening = false;

  buttonPosition = { top: 0, left: 0 };

  ngOnInit(): void {
    this.menuSearch.loadOptions();
    this.cargarPerfilMenu();
    const pollMs = this.backendNotifications ? 60_000 : 30_000;
    merge(
      of(undefined),
      this.notificationService.onNotificacionRecibida,
      interval(pollMs),
      this.notificationCountRefresh$,
    )
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const b = this.backendNotifications;
          if (!b) {
            return of(this.countUnreadFromLocalStorage());
          }
          return b.list(true).pipe(
            map((rows) => rows.length),
            catchError(() => of(this.countUnreadFromLocalStorage())),
          );
        }),
      )
      .subscribe((n) => this.notificationCount.set(n));
  }


  onGlobalSearch(query: string): void {
    const q = (query || '').trim();
    if (q.length < 2) {
      this.searchResults.set([]);
      this.showSearchResults.set(false);
      return;
    }
    this.searchResults.set(this.menuSearch.search(q));
    this.showSearchResults.set(true);
    this.cdr.markForCheck();
  }

  onSelectSearchResult(option: VaultMenuSearchOption): void {
    const result = this.menuSearch.openOption(option);
    if (result === 'limit') {
      this.showSearchToast(
        `Máximo ${VAULT_MAX_OPEN_TABS} pestañas abiertas. Cierra alguna para abrir otra.`,
      );
      return;
    }
    if (result === 'denied') {
      this.showSearchToast('No tienes acceso a esta opción.');
      return;
    }
    this.closeSearchResults();
  }

  closeSearchResults(): void {
    this.showSearchResults.set(false);
    this.searchResults.set([]);
  }

  private showSearchToast(msg: string): void {
    this.searchToast.set(msg);
    if (this.searchToastTimeout) clearTimeout(this.searchToastTimeout);
    this.searchToastTimeout = setTimeout(() => {
      this.searchToast.set(null);
      this.cdr.markForCheck();
    }, 2500);
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  onSearchKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
      event.preventDefault();
      const input = this.elementRef.nativeElement.querySelector(
        'lib-header input',
      ) as HTMLInputElement | null;
      input?.focus();
    }
    if (event.key === 'Escape' && this.showSearchResults()) {
      event.preventDefault();
      this.closeSearchResults();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const outside = !this.elementRef.nativeElement.contains(event.target);
    if (outside && this.showSearchResults()) {
      this.closeSearchResults();
    }
  }

  /** Carga nombre (auth/me) + roles (JWT) para el menú de perfil. */
  private cargarPerfilMenu(): void {
    const roles = this.authService.getUserRoles() ?? [];
    this.userRoles.set(
      roles
        .map((r: { name?: string; authority?: string; description?: string }) => {
          const key = r?.description || r?.name || r?.authority || '';
          return String(key).trim();
        })
        .filter((label: string) => !!label)
        .join(', '),
    );

    this.authMe
      .fetchRaw$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((raw) => {
        const name = this.extractDisplayName(raw);
        this.userFullName.set(name);
      });
  }

  private extractDisplayName(raw: unknown): string {
    if (!raw || typeof raw !== 'object') return '';
    const root = raw as Record<string, unknown>;
    const data =
      root['data'] && typeof root['data'] === 'object'
        ? (root['data'] as Record<string, unknown>)
        : root;
    const full =
      (typeof data['full_name'] === 'string' && data['full_name'].trim()) ||
      (typeof data['name'] === 'string' && data['name'].trim()) ||
      '';
    if (full && full !== this.userName) return full;
    const email = typeof data['email'] === 'string' ? data['email'].trim() : '';
    return email && email !== this.userName ? email : '';
  }

  onNotificationsClick(): void {
    const open = this.openNotificationPanel;
    if (!open) return;

    const anchor =
      document.querySelector<HTMLElement>(
        '.top-header lib-header .notifications-container button.icon-btn[aria-label="Ver notificaciones"]',
      ) ??
      document.querySelector<HTMLElement>(
        '.top-header lib-header .notifications-container button.icon-btn',
      );

    open({ modoOscuro: this.isDarkMode, anchor })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.notificationCountRefresh$.next());
  }

  private countUnreadFromLocalStorage(): number {
    try {
      const raw = JSON.parse(localStorage.getItem('notificaciones_reales') || '[]') as Array<{
        leida?: boolean;
      }>;
      return raw.filter((x) => !x.leida).length;
    } catch {
      return 0;
    }
  }

  showMenu(headerRef?: any): void {
    if (this.isMenuOpening) return;
    this.isMenuOpening = true;

    Promise.resolve().then(() => this.profileClick.emit());

    let targetElement: HTMLElement | null = null;
    if (headerRef?.elementRef?.nativeElement) {
      targetElement = headerRef.elementRef.nativeElement.querySelector('.user-profile');
    }
    if (!targetElement) {
      targetElement = document.querySelector('.user-profile');
    }

    if (!targetElement || !this.triggerButton?.nativeElement || !this.menuTrigger) {
      this.isMenuOpening = false;
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const button = this.triggerButton.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      button.removeAttribute('style');
      button.style.position = 'fixed';
      button.style.top = `${rect.bottom + this.MENU_OFFSET}px`;
      button.style.left = `${rect.right - this.MENU_WIDTH}px`;
      button.style.width = `${this.MENU_WIDTH}px`;
      button.style.height = '1px';
      button.style.opacity = '0';
      button.style.pointerEvents = 'none';
      button.style.zIndex = '-1';
    });

    this.menuTrigger.updatePosition();

    setTimeout(() => {
      this.ngZone.run(() => {
        if (this.menuTrigger && !this.menuTrigger.menuOpen) {
          this.menuTrigger.openMenu();
        }
        this.isMenuOpening = false;
      });
    }, 0);
  }

  onDarkModeToggle(): void {
    this.darkModeToggle.emit();
  }

  onProfileAction(item: { id: number; action: string }): void {
    switch (item.id) {
      case 1:
        this.onLogout();
        break;
    }
  }

  onLogout(): void {
    this.menuSearch.invalidate();

    this.logout.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchToastTimeout) clearTimeout(this.searchToastTimeout);
  }
}
